import Image from 'next/image'
import { Check } from 'lucide-react'

interface AuthBrandPanelProps {
  /** The one-line argument this page makes. */
  headline: string
  /** Three-ish supporting points. Kept short — they wrap badly past ~60 chars. */
  points: string[]
}

/**
 * The teal half of the logged-out split layout, shared by sign-in and sign-up.
 * For a vendor arriving cold from an invite email this is the first thing they
 * ever see of Partnr, so each page passes copy suited to why that visitor is here.
 *
 * The form half is deliberately NOT shared — the two pages have different fields,
 * different validation and different success behaviour.
 */
export function AuthBrandPanel({ headline, points }: AuthBrandPanelProps) {
  return (
    <div className="relative flex flex-col justify-between overflow-hidden bg-brand px-8 py-10 lg:px-14 lg:py-14">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-white/[0.06]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full bg-white/[0.04]"
      />

      {/* The wordmark ships in its teal; reversed to white to sit on the brand panel. */}
      <Image
        src="/partnr-wordmark.png"
        alt="Partnr"
        width={800}
        height={280}
        priority
        className="relative h-auto w-[136px] brightness-0 invert"
      />

      <div className="relative mt-10 lg:mt-0">
        <h2 className="max-w-[420px] text-[26px] font-bold leading-[1.25] text-white lg:text-[34px]">
          {headline}
        </h2>
        <ul className="mt-8 space-y-4">
          {points.map(point => (
            <li key={point} className="flex items-start gap-3 text-[14px] leading-6 text-white/85">
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white/15">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </span>
              {point}
            </li>
          ))}
        </ul>
      </div>

      <p className="relative mt-10 hidden text-[13px] text-white/50 lg:block">
        © {new Date().getFullYear()} Partnr
      </p>
    </div>
  )
}
